import * as Backbone from "backbone";
import {CollectionFetchOptions} from "backbone";
import * as _ from "lodash";
import {InsecureCachingCollection} from "./_BaseCollection";

export interface PermissionFunction {
    (id: number): boolean;

    query(id): string;

    isPermissionFunction: boolean;
}

export interface PermissionCollectionOptions extends CollectionFetchOptions {
    userGroupId: number;
    currentUser?: any; // Optional current user reference for superuser checks
}

export class PermissionCollection extends InsecureCachingCollection<Backbone.Model> {

    userGroupId: number;
    currentUser?: any; // Reference to current user for superuser check

    constructor(initialModels?: Object[] | any[], options?: PermissionCollectionOptions) {
        super(initialModels, options);

        if (options) {
            this.userGroupId = options.userGroupId;
            this.currentUser = options.currentUser; // Store reference to current user
        }
        this.getViewableInstallIds = _.memoize(this.getViewableInstallIds);
        this.getViewableDashboardIds = _.memoize(this.getViewableDashboardIds);
    }

    url = () => {
        var id = this.userGroupId ? '/' + this.userGroupId : '';
        return window.location.origin + '/api/v1/permissions' + id;
    };

    /**
     * Check if the current user is a superuser
     * SECURITY NOTE: This is only for UI optimization, not security
     * All actual permission checks must be validated on the backend
     * @returns {boolean} True if user is superuser, false otherwise
     */
    isSuperuser(): boolean {
        const isSuperuser = this.currentUser && this.currentUser.get && this.currentUser.get('isSuperuser') === true;
        return isSuperuser;
    }

    /**
     * Override getCollectionFetchPromise to skip fetching permissions for superusers
     * SECURITY NOTE: This is only for performance optimization
     * The backend still validates all permissions on every API call
     */
    getCollectionFetchPromise(options?: Backbone.CollectionFetchOptions) {
        // If user is superuser, skip the fetch and return resolved promise
        // This is safe because:
        // 1. Backend still validates permissions on every API call
        // 2. This only affects UI permission checks, not actual data access
        // 3. Malicious users can't access data they shouldn't have
        if (this.isSuperuser()) {
            var d = $.Deferred();
            // Mark as fetched and trigger sync event for consistency
            this.numFetched = 1;
            setTimeout(() => {
                this.trigger('sync', this, [], options);
                d.resolve();
            }, 0);
            return d;
        }
        
        // For non-superusers, use the parent implementation
        return super.getCollectionFetchPromise(options);
    }

    /**************************************************************************************************************
     * Permission Check Functions
     *
     *************************************************************************************************************/
    protected generatePermissionFunction(type, subtype, action): PermissionFunction {
        var f = function (id) {
            // Superuser bypass: if current user is superuser, always return true
            if (this.isSuperuser()) {
                return true;
            }
            
            var req = { type: type, subtype: subtype, action: action };
            if (id) req['references'] = id;
            return Boolean(this.findWhere(req))
        };
        f['query'] = function (id) {
            return type + ':' + id + ':' + (subtype ? subtype + ':' : '') + action;
        };
        f['isPermissionFunction'] = true;
        f['bind'] = function (that) {
            var g = _.bind(f, that);
            g['query'] = _.bind(f['query'], that);
            g['isPermissionFunction'] = f['isPermissionFunction'];
            g['bind'] = f['bind'];
            return g;
        };
        return <any>f;
    }

    // Organization
    canCreateNewOrg = this.generatePermissionFunction('org', null, 'create');
    canViewOrganization = this.generatePermissionFunction('org', null, 'view');
    canModifyOrganization = this.generatePermissionFunction('org', null, 'modify');
    canSetHighFrequencySampling = this.generatePermissionFunction('org', 'install', 'set_hf_sampling');

    // Install
    canCreateNewSite = this.generatePermissionFunction('org', 'install', 'create');
    canViewInstall = this.generatePermissionFunction('install', null, 'view');
    canModifyInstall = this.generatePermissionFunction('install', null, 'modify');
    canDeleteInstall = this.generatePermissionFunction('install', null, 'delete');

    canViewSystemStatus = this.generatePermissionFunction('install', 'ss', 'view');
    canViewInstallOverrides = this.generatePermissionFunction('install', 'ovrd', 'view');
    canModifyInstallOverrides = this.generatePermissionFunction('install', 'ovrd', 'modify');

    canCreateInstallLayout = this.generatePermissionFunction('install', 'layout', 'create');
    canViewInstallLayout = this.generatePermissionFunction('install', 'layout', 'view');
    canModifyInstallLayout = this.generatePermissionFunction('install', 'layout', 'modify');
    canDeleteInstallLayout = this.generatePermissionFunction('install', 'layout', 'delete');

    // Dashboard
    canCreateNewDashboard = this.generatePermissionFunction('org', 'db', 'create');
    canViewDashboard = this.generatePermissionFunction('db', null, 'view');
    canModifyDashboard = this.generatePermissionFunction('db', null, 'modify');
    canDeleteDashboard = this.generatePermissionFunction('db', null, 'delete');

    // Report
    canCreateNewReport = this.generatePermissionFunction('org', 'report', 'create');
    canViewReport = this.generatePermissionFunction('report', null, 'view');
    canModifyReport = this.generatePermissionFunction('report', null, 'modify');
    canDeleteReport = this.generatePermissionFunction('report', null, 'delete');

    // Settings
    canViewSensorConfig = this.generatePermissionFunction('install', 'sc', 'view');
    canModifySensorConfig = this.generatePermissionFunction('install', 'sc', 'modify');
    canViewChannelConfig = this.generatePermissionFunction('install', 'cc', 'view');
    canModifyChannelConfig = this.generatePermissionFunction('install', 'cc', 'modify');

    canViewInstallSettings(installId?: number) {
        // return false;
        return (this.canViewSensorConfig(installId)
            || this.canViewChannelConfig(installId))
            && this.canViewInstall(installId);
    };

    canModifyInstallSettings(installId?: number) {
        // return false;
        return (this.canModifySensorConfig(installId)
            || this.canModifyChannelConfig(installId))
            && this.canModifyInstall(installId);
    };

    // Permission
    canViewUserGroupPermissions = this.generatePermissionFunction('perm', null, 'view');
    canModifyUserGroupPermissions = this.generatePermissionFunction('perm', null, 'modify');

    // UserGroups
    canCreateNewUserGroup = this.generatePermissionFunction('org', 'ug', 'create');
    canViewUserGroup = this.generatePermissionFunction('ug', null, 'view');
    canModifyUserGroup = this.generatePermissionFunction('ug', null, 'modify');
    canDeleteUserGroup = this.generatePermissionFunction('ug', null, 'delete');
    canAddUserToUserGroup = this.generatePermissionFunction('ug', 'user', 'create');

    // Apps
    canViewApp = this.generatePermissionFunction('app', null, 'view');
    canModifyApp = this.generatePermissionFunction('app', null, 'modify');
    canDeleteApp = this.generatePermissionFunction('app', null, 'delete');


    /**************************************************************************************************************
     *  ID Getter Functions
     *
     *************************************************************************************************************/

    getViewableInstallIds() {
        return _.uniq(_.map(
            this.where({ type: 'install', subtype: null, action: 'view' }),
            function (perm) {
                return perm.get('references')
            }
        ));
    };

    getViewableDashboardIds() {
        return _.uniq(_.map(
            this.where({ type: 'db', subtype: null, action: 'view' }),
            function (perm) {
                return perm.get('references')
            }
        ));
    };
}





// WEBPACK FOOTER //
// ./srv/public/js/collections/Permissions.ts